import { Form, Row, Col } from "react-bootstrap";
import { FaArrowLeft } from "react-icons/fa";
import "../../ExtractAnalysis/ExtractAnalysisV2.css";

import BonusRangeTable from "../RangeTable/BonusRangeTable";
import BonusDocumentTable from "../DocumentTable/BonusDocumentTable";
import GenericSmallTable from "../GenericTable/GenericSmallTable";
import DeducaoDocumentTable, { type DadosDeducao } from "../DeducaoTable/DeducaoTable";
import ValoresJaSolicitadosTable from "../ValoresJaSolicitadosTable/ValoresJaSolicitadosTable";
import { hasDeducaoAtrasoPreenchida, type ValoresSolicitadosView } from "../../V2/mapExtratoSolicitacaoApi";

interface DadosSindicato {
  nome?: string;
  cnpj?: string;
  sigla?: string;
}

interface ValidacaoSindicato {
  observacaoSindicato: string;
  respondidoSindicatoEm: string;
}

interface CriterioBonus {
  documento?: string;
  nome?: string;
  conforme?: boolean;
  percentual?: number;
  valor?: string | number;
}

interface FaixaReceitaBonus {
  titulo?: string;
  receitaMin?: number;
  receitaMax?: number;
  valorMinimo?: number;
  valorBonusPotencial?: number;
  valorMaximoPotencial?: number;
}

export interface PreviewExtratoV2 {
  receitaOperacional?: number;
  faixaReceitaBonus?: FaixaReceitaBonus;
  valorMinimo?: number;
  valorBonus?: number;
  valorBonusExtra?: number;
  valorTotalApoioBruto?: number;
  criterioBonus?: CriterioBonus[];
  criterioBonusExtra?: CriterioBonus[];
}

export interface ExtratoConteudoV2Props {
  dadosSindicato: DadosSindicato;
  validacaoSindicato?: ValidacaoSindicato | null;
  previewData: PreviewExtratoV2 | null;
  deducaoData: DadosDeducao | null;
  valoresSolicitados?: ValoresSolicitadosView | null;
  valorLiquidoApoioApi?: number | null;
  onUpdateDeducoes?: (dados: DadosDeducao) => void;
  mostrarErrosOutras?: boolean;
  disabled?: boolean;
  isInapto?: boolean;
  showPageHeader?: boolean;
  solicitacaoLabel?: string;
  selectedYear?: number | null;
  onNavigateBack?: () => void;
}

const formatCNPJ = (cnpj: string | undefined): string => {
  if (!cnpj) return "";
  const digits = cnpj.replace(/\D/g, "");
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

const formatBRL = (valor: number | undefined | null): string => {
  if (valor == null) return "R$ 0,00";
  return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const ExtratoConteudoV2 = ({
  dadosSindicato,
  validacaoSindicato,
  previewData,
  deducaoData,
  valoresSolicitados,
  valorLiquidoApoioApi,
  onUpdateDeducoes = () => {},
  mostrarErrosOutras = false,
  disabled = false,
  isInapto = false,
  showPageHeader = false,
  solicitacaoLabel,
  selectedYear,
  onNavigateBack,
}: ExtratoConteudoV2Props) => {
  const numeroSolicitacao = Number(/^\s*(\d+)/.exec(solicitacaoLabel ?? "")?.[1] ?? 1);
  const isSegundaSolicitacaoOuPosterior = numeroSolicitacao >= 2;
  const temCriterioBonus = !!previewData?.criterioBonus?.length;
  const temCriterioBonusExtra = !!previewData?.criterioBonusExtra?.length;

  const dadosResumoApoio = [
    ...(isSegundaSolicitacaoOuPosterior
      ? []
      : [{ label: "VALOR MÍNIMO", valor: formatBRL(previewData?.valorMinimo) }]),
    ...(temCriterioBonus
      ? [{ label: "BÔNUS ALCANÇADO", valor: formatBRL(previewData?.valorBonus) }]
      : []),
    ...(temCriterioBonusExtra
      ? [{ label: "BÔNUS EXTRA ALCANÇADO", valor: formatBRL(previewData?.valorBonusExtra) }]
      : []),
    { label: "VALOR TOTAL DO APOIO", valor: formatBRL(previewData?.valorTotalApoioBruto) },
  ];

  const titleBonus: [string, string, string, string] = ["DOCUMENTO BÔNUS", "CONFORME?", "PERCENTUAL", "VALOR"];
  const titleBonusExtra: [string, string, string, string] = ["DOCUMENTO BÔNUS EXTRA", "CONFORME?", "PERCENTUAL", "VALOR"];

  return (
    <>
      {/* Banner FIEMG */}
      <div className="v2-pdf-banner">
        <div className="v2-pdf-banner-logo">
          <svg width="80" height="28" viewBox="0 0 473 164" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M160.32 38.65C160.59 36.89 159.51 36.08 158.02 36.08H135.84C134.35 36.08 133 37.16 132.73 38.65L128.5 62.66L158.57 48.57L160.31 38.66L160.32 38.65Z" fill="white"/>
            <path d="M126.6 73.42L115.95 133.87C115.68 135.22 116.49 136.58 118.11 136.58H140.29C142.32 136.58 143.27 135.36 143.54 133.87L156.67 59.33L126.6 73.42Z" fill="white"/>
            <path d="M120.67 38.6699L117.69 55.1699C117.42 56.6599 116.07 57.8699 114.58 57.8699H87.2601C82.3901 57.8699 80.5001 59.4899 79.5501 64.6299L77.7901 74.37H111.06C112.41 74.37 113.36 75.32 113.36 76.6699V77.08L110.52 93.04C110.25 94.53 108.9 95.7399 107.41 95.7399H74.14L67.5101 133.34C67.2401 134.83 65.7501 136.04 64.2601 136.04H42.21C40.86 136.04 40.05 135.09 40.05 133.88V133.34L52.09 64.6299C56.82 37.1699 74.0001 34.2 92.1201 34.2C105.24 34.2 113.36 35.2799 118.36 35.9599C119.85 36.0999 120.66 36.9099 120.66 38.1199V38.66L120.67 38.6699Z" fill="white"/>
            <path d="M237.93 38.5301L235.09 55.0301C234.82 56.5201 233.33 57.7301 231.84 57.7301H204.52C199.65 57.7301 197.76 59.3501 196.81 64.4901L195.05 74.3601H228.32C229.67 74.3601 230.62 75.3101 230.62 76.6601V77.0701L227.78 93.0301C227.51 94.5201 226.16 95.7301 224.67 95.7301H191.4L189.37 106.96C189.23 108.04 189.1 108.85 189.1 109.66C189.1 112.77 190.72 113.72 194.65 113.72H221.97C223.32 113.72 224.27 114.67 224.27 116.02V116.43L221.29 132.93C221.02 134.55 219.94 135.36 218.18 135.63C212.91 136.31 204.65 137.25 191.53 137.25C175.71 137.25 161.23 131.57 161.23 113.85C161.23 111.69 161.5 109.39 161.91 106.95L169.35 64.4801C174.08 37.1601 190.31 34.1801 209.38 34.1801C222.5 34.1801 230.75 35.1301 235.75 35.8001C237.24 35.9401 237.91 36.6101 237.91 37.9601V38.5001L237.93 38.5301Z" fill="white"/>
            <path d="M346.27 40.56L329.77 134.43C329.5 135.92 328.28 136.05 327.2 136.05H305.02C304.07 136.05 303.53 135.1 303.53 134.02V133.34L312.19 83.7C312.19 83.3 312.33 83.02 312.33 82.89C312.33 82.48 312.19 82.35 312.06 82.35C311.93 82.35 311.79 82.49 311.66 82.62H311.52C311.25 82.62 310.3 83.84 309.9 84.38L295.97 102.37C294.62 104.13 293.27 104.94 291.64 104.94H282.31C280.69 104.94 279.61 104.13 278.79 102.37L270.95 83.84C270.68 83.3 270.54 82.62 270.27 82.62H270.13C269.99 82.48 269.86 82.35 269.73 82.35C269.46 82.35 269.05 82.76 268.92 83.7L260.26 133.34C259.99 134.83 258.91 136.04 257.83 136.04H235.51C234.56 136.04 233.62 135.9 233.62 134.82V134.41L250.12 40.54C250.66 37.84 253.37 35.54 256.07 35.54H270.14C273.52 35.54 275.28 37.03 276.36 39.6L289.89 73.82C290.29 74.77 290.57 75.44 291.11 75.44C291.65 75.44 292.19 74.76 293 73.82L318.7 39.6C320.59 37.03 322.89 35.54 326.27 35.54H342.23C344.67 35.54 346.42 37.3 346.42 39.6C346.42 40.01 346.28 40.28 346.28 40.55L346.27 40.56Z" fill="white"/>
            <path d="M432.69 40.42L429.58 57.46C429.31 59.08 428.77 59.62 427.41 59.62H427.01C421.87 59.35 411.05 58.4 401.04 58.4C387.51 58.4 381.83 62.46 378.86 78.69L376.29 93.03C375.75 96.41 375.48 99.12 375.48 101.55C375.48 111.96 380.62 113.72 385.62 113.72C388.19 113.72 390.89 113.59 393.19 113.32L398.06 85.59C398.33 84.1 399.55 82.89 401.04 82.89H422.41C423.76 82.89 424.71 83.84 424.71 85.19V85.6L416.73 130.91C416.32 132.94 415.65 133.61 413.35 134.02C405.23 135.64 394.55 137.27 381.43 137.27C366.28 137.27 347.48 128.48 347.48 103.59C347.48 100.35 347.75 96.69 348.43 92.91L351 78.71C358.17 39.35 381.7 34.21 401.58 34.21C415.65 34.21 425.25 36.37 430.52 37.86C431.87 38.27 432.82 38.54 432.82 39.75C432.82 39.89 432.68 40.15 432.68 40.43L432.69 40.42Z" fill="white"/>
          </svg>
        </div>
        <span className="v2-pdf-banner-title">EXTRATO PROGRAMA MERITOCRACIA</span>
        <div className="v2-pdf-banner-spacer" />
      </div>

      {/* Cabeçalho da página (apenas na página, não no modal) */}
      {showPageHeader && (
        <div className="v2-page-header">
          <h1 className="v2-page-title">EXTRATO</h1>
          <div className="d-flex flex-column align-items-end gap-1">
            <div
              className="merit-voltar-seta"
              onClick={onNavigateBack}
              title="Voltar para a lista"
            >
              <FaArrowLeft />
            </div>
            <span className="v2-solicitacao-ref">
              {solicitacaoLabel || "Solicitação"} - {selectedYear}
            </span>
          </div>
        </div>
      )}

      {/* MOTIVO DA CONTESTAÇÃO */}
      {validacaoSindicato && (
        <div className="v2-card v2-contestacao-card">
          <div className="v2-card-body">
            <div className="v2-card-section-title">MOTIVO DA CONTESTAÇÃO DO EXTRATO</div>
            <Form.Control
              as="textarea"
              rows={4}
              value={validacaoSindicato.observacaoSindicato}
              disabled
              className="v2-contestacao-textarea"
            />
            <div className="v2-contestacao-data">
              {new Date(validacaoSindicato.respondidoSindicatoEm).toLocaleString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }).replace(",", " -")}
            </div>
          </div>
        </div>
      )}

      {/* DADOS DO SINDICATO */}
      <div className="v2-card">
        <div className="v2-card-body">
          <div className="v2-card-section-title">DADOS DO SINDICATO</div>
          <div className="mb-3">
            <div className="v2-field-label">NOME DO SINDICATO</div>
            <Form.Control type="text" value={dadosSindicato.nome ?? ""} disabled />
          </div>
          <Row className="gy-3">
            <Col md={6}>
              <div>
                <div className="v2-field-label">CNPJ</div>
                <Form.Control type="text" value={formatCNPJ(dadosSindicato.cnpj)} disabled />
              </div>
            </Col>
            <Col md={6}>
              <div>
                <div className="v2-field-label">SIGLA</div>
                <Form.Control type="text" value={dadosSindicato.sigla ?? ""} disabled />
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* VALOR DA RECEITA */}
      <div className="v2-card">
        <div className="v2-card-body">
          <div className="v2-card-section-title">VALOR DA RECEITA</div>
          <Row>
            <Col md={6}>
              <div className="v2-field-label">RECEITA OPERACIONAL</div>
              <Form.Control
                type="text"
                value={previewData?.receitaOperacional != null ? formatBRL(previewData.receitaOperacional) : "R$ 0,00"}
                disabled
              />
            </Col>
          </Row>
        </div>
      </div>

      {/* TABELA DE FAIXA */}
      {!isSegundaSolicitacaoOuPosterior && (
        <div className="v2-card">
          <div className="v2-card-body v2-faixa-body">
            <BonusRangeTable
              tituloFaixa={previewData?.faixaReceitaBonus?.titulo}
              receitaMin={previewData?.faixaReceitaBonus?.receitaMin}
              receitaMax={previewData?.faixaReceitaBonus?.receitaMax}
              valorMinimo={previewData?.faixaReceitaBonus?.valorMinimo}
              valorBonusPotencial={previewData?.faixaReceitaBonus?.valorBonusPotencial}
              valorMaximoPotencial={previewData?.faixaReceitaBonus?.valorMaximoPotencial}
            />
          </div>
        </div>
      )}

      {/* BÔNUS */}
      <div className="v2-card v2-bonus-section">
        <div className="v2-card-body">
          <div className="v2-card-section-title">BÔNUS</div>
          {!!previewData?.criterioBonus?.length && (
            <BonusDocumentTable data={previewData.criterioBonus} title={titleBonus} />
          )}
          {!!previewData?.criterioBonusExtra?.length && (
            <BonusDocumentTable data={previewData.criterioBonusExtra} title={titleBonusExtra} />
          )}
          <GenericSmallTable dados={dadosResumoApoio} corValor="verde" />
        </div>
      </div>

      {/* DEDUÇÃO */}
      {!isSegundaSolicitacaoOuPosterior && (
        <div className="v2-card v2-deducao-section">
          <div className="v2-card-body">
            <div className="v2-card-section-title">DEDUÇÃO</div>
            <DeducaoDocumentTable
              atrasoDocs={
                hasDeducaoAtrasoPreenchida(deducaoData?.deducaoAtraso)
                  ? [deducaoData!.deducaoAtraso]
                  : []
              }
              titulosEmAberto={deducaoData?.deducaoTitulos ?? []}
              outrasDeducoes={deducaoData?.deducaoOutros ?? []}
              totalBrutoBonus={formatBRL(previewData?.valorTotalApoioBruto)}
              onUpdateDeducoes={onUpdateDeducoes}
              mostrarErrosOutras={mostrarErrosOutras}
              disabled={disabled}
              initialTotalLiquido={valorLiquidoApoioApi ?? undefined}
              keepTotalFixed={isInapto}
            />
          </div>
        </div>
      )}

      {/* VALORES JÁ SOLICITADOS */}
      {valoresSolicitados && (
        <div className="v2-card v2-valores-section">
          <div className="v2-card-body">
            <div className="v2-card-section-title">VALORES JÁ SOLICITADOS</div>
            <ValoresJaSolicitadosTable
              itens={valoresSolicitados.itens}
              total={valoresSolicitados.total}
              valorLiquidoApoio={valoresSolicitados.valorLiquidoApoio}
              hideLiquidoApoio
            />
          </div>
        </div>
      )}

      {/* DATA DE EMISSÃO */}
      <div className="v2-pdf-date">
        Belo Horizonte,{" "}
        {new Date().toLocaleDateString("pt-BR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </div>
    </>
  );
};

export default ExtratoConteudoV2;
